import { clsx } from 'clsx';

import type { TooltipComponentProps } from '../../types/TooltipProps';

import { TooltipIcon } from './TooltipIcon';

import styles from './Tooltip.module.css';

/**
 * Format a number without unnecessary decimal places.
 * If the number is a whole number, don't show decimals.
 * Otherwise, show up to 2 decimal places.
 */
function formatNumberWithoutUnnecessaryDecimals(value: number): string {
  // Check if the number is a whole number (no decimal part)
  if (Number.isInteger(value)) {
    return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
  }
  // For numbers with decimals, show up to 2 decimal places
  return value.toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 0 });
}

/**
 * Default tooltip component used when no custom formatter or component is provided.
 * Displays a formatted label, a list of series with their values and colors,
 * and optionally reference line events at the bottom.
 */
export function DefaultTooltip({
  label,
  series,
  seriesColorMap,
  eventGroups,
  yAxisValueFormatter,
  renderTooltipSecondaryRow,
  renderTooltipFooter,
  rowIcon,
}: TooltipComponentProps) {
  const hasSeries = series.length > 0;
  const hasEventGroups = eventGroups && eventGroups.length > 0;

  return (
    <div className={styles.tooltip}>
      <div className={styles.tooltipLabel}>{label}</div>
      {hasSeries && (
        <div className={styles.tooltipSeriesContainer}>
          {series.map((s) => {
            // Use custom formatter if provided, otherwise use default formatting
            const formattedValue = yAxisValueFormatter
              ? yAxisValueFormatter(s.value, s)
              : formatNumberWithoutUnnecessaryDecimals(s.value);
            const customContent = renderTooltipSecondaryRow?.(s.dataPoint, s.dataKey);

            return (
              <div key={s.key} className={styles.tooltipRow}>
                <div className={styles.tooltipSeriesItem}>
                  <span className={styles.tooltipSeriesNameContainer}>
                    <TooltipIcon name={s.rowIcon} color={s.color} />
                    <span className={styles.tooltipSeriesName}>{s.name}</span>
                  </span>
                  <span className={styles.tooltipSeriesValue}>{formattedValue}</span>
                </div>
                {customContent && <span className={styles.tooltipSeriesSecondary}>{customContent}</span>}
              </div>
            );
          })}
        </div>
      )}
      {hasEventGroups &&
        eventGroups.map((group, groupIndex) => {
          const hasEvents = group.events.length > 0;
          // Show section if there's a name or events (or both)
          if (!group.tooltipName && !hasEvents) {
            return null;
          }

          return (
            <div key={`event-group-${groupIndex}`} className={styles.tooltipEventsSection}>
              {group.tooltipName && <div className={styles.tooltipEventsHeader}>{group.tooltipName}</div>}
              {hasEvents && (
                <div className={styles.tooltipEventsList}>
                  {group.events.map((event, eventIndex) => {
                    const seriesColor = event.seriesKey ? seriesColorMap?.get(event.seriesKey) : undefined;
                    const hasMultipleEvents = group.events.length > 1;
                    const showBullet = hasMultipleEvents && !seriesColor;
                    const hasIndicator = seriesColor || showBullet;
                    return (
                      <div key={`event-${eventIndex}`} className={styles.tooltipEventItem}>
                        {seriesColor && (
                          <TooltipIcon name={rowIcon} color={seriesColor} className={styles.tooltipEventDiamond} />
                        )}
                        {showBullet && <span className={styles.tooltipEventBullet}>●</span>}
                        <span
                          className={clsx(
                            styles.tooltipEventDescription,
                            !hasIndicator && styles.tooltipEventDescriptionFullWidth,
                          )}
                        >
                          {event.eventDescription}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      {(() => {
        const footerContent = renderTooltipFooter?.(series);
        return footerContent ? <div className={styles.tooltipFooter}>{footerContent}</div> : null;
      })()}
    </div>
  );
}
